<script setup>
import { useGlobalStore } from 'src/composables/useGlobalStore'
import { useRoute } from 'vue-router'
import { inject, ref, watch, onMounted } from 'vue'
import HeaderMenuItem from 'components/general/HeaderMenuItem'
import { useWindowScroll } from '@vueuse/core'
import RaumpioniereLogo from 'components/svg/RaumpioniereLogo'
import { useGA } from '#/useGA'
import debounce from 'debounce'
import gsap from 'gsap'
import InsightsSelector from 'components/insights/InsightsSelector.vue'

const { event } = useGA()

const { isLoggedIn, user, logout, showMobileDrawer, isProduction } = useGlobalStore()
const { screen } = inject('Shared')
const route = useRoute()
const { x, y } = useWindowScroll()
const goSticky = ref(route.path !== '/')
watch([y, screen, route], () => {
  if (route.path !== '/') {
    goSticky.value = true
    return
  }
  goSticky.value = y.value > 30
})

const doLogOut = () => {
  logout(false)
}

const navigationEvent = (path, eventLabel) => {
  if (route.fullPath !== path) {
    event('navigation', { eventCategory: 'navigation', eventLabel: eventLabel })
  }
}
const position = ref()
function calcPosition () {
  if (route.path === '/') {
    if (y.value === 0) {
      position.value = null
      return
    }
    position.value = 'fixed'
    return
  }
  if (y.value > 0) {
    position.value = 'sticky'
  }
}
calcPosition()
watch([y, route], calcPosition)

function scrollToContact () {
  const element = document.querySelector('#kontakt')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
  }
}

const popupHovered = ref(false)
const goWhiteMenu = ref(false)
let hideInsightsMenu = () => {
  if (!popupHovered.value) {
    toggleInsightsMenu(false)
  }
}
hideInsightsMenu = debounce(hideInsightsMenu, 150)

let closeAnim
let openAnim
function toggleInsightsMenu (open) {
  if (open && route.path === '/') {
    goWhiteMenu.value = true
  }
  if (open) {
    if (closeAnim) {
      closeAnim.kill()
    }
    openAnim = gsap.to('.insights-menu', {
      delay: goSticky.value ? 0 : 0.25,
      duration: 0.5,
      padding: '20px 0 ',
      maxHeight: '534px',
      onComplete: () => {
        goWhiteMenu.value = true
      }
    })
  } else {
    if (openAnim) {
      openAnim.kill()
    }
    closeAnim = gsap.to('.insights-menu', {
      duration: 0.5,
      padding: '0px',
      maxHeight: '0px',
      onComplete: () => {
        goWhiteMenu.value = false
      }
    })
  }
}
</script>

<template lang="pug">
.app-header--container(:class="(goWhiteMenu || goSticky) ? 'app-header--container-white' : ''" :style="`position: ${(y === 0 && route.path === '/') ? 'absolute' : position}`")
  .app-header--content
    .app-header-menu
      router-link(to="/" @click="navigationEvent('/', 'B2C')").app-header-link
        raumpioniere-logo(:fill="(goWhiteMenu || goSticky) ? 'black' : 'white'" width-mobile="94px" height-mobile="27.51px")
      .app-header--menu-container
        header-menu-item(:color="(goWhiteMenu || goSticky) ? 'black' : 'white'" label="Referenzen" :route-names="['References']" to='/referenzen' @click="navigationEvent('/referenzen', 'References')")
        header-menu-item(:color="(goWhiteMenu || goSticky) ? 'black' : 'white'" label="Lösungen" :route-names="['Solutions']" to='/solutions' @click="navigationEvent('/solutions', 'Solutions')")
        header-menu-item(
          :color="(goWhiteMenu || goSticky) ? 'black' : 'white'"
          label="Insights"
          :route-names="['Insights', 'Glossary', 'Blogs', 'Blog']"
          @mouseover="toggleInsightsMenu(true)"
          @mouseleave="hideInsightsMenu()"
          to='/insights'
          @click="navigationEvent('/insights', 'Insights'); toggleInsightsMenu(false)"
        )
        header-menu-item(:color="(goWhiteMenu || goSticky) ? 'black' : 'white'" label="Über uns" :route-names="['About Us']" to='/about-us' @click="navigationEvent('/about-us', 'About Us')")
        header-menu-item(:color="(goWhiteMenu || goSticky) ? 'black' : 'white'" label="Kontakt" @click="scrollToContact" :route-names="[]")
        header-menu-item(:color="(goWhiteMenu || goSticky) ? 'black' : 'white'" :large-color="(goWhiteMenu || goSticky) ? '#1F47CD' : 'white'" label="Jetzt Potenzial prüfen" :route-names="['Analyzer']" :to="`/${isLoggedIn ? 'b2b' : 'b2c'}/analyzer`" :large="true" @click="navigationEvent('/analyzer', 'Analyzer')")
        .loggedOutBtn(v-if="isLoggedIn")
          svg(width='16' height='21' viewBox='0 0 16 21' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(d='M8 0C5.21812 0 2.96296 2.28335 2.96296 5.1C2.96296 7.91665 5.21812 10.2 8 10.2C10.7819 10.2 13.037 7.91665 13.037 5.1C13.037 2.28335 10.7819 0 8 0Z' fill='#22223C')
            path(d='M10.9725 12.4689C9.0033 12.1507 6.9967 12.1507 5.02747 12.4689L4.8164 12.503C2.04169 12.9514 0 15.3747 0 18.2196C0 19.7552 1.22944 21 2.74604 21H13.254C14.7706 21 16 19.7552 16 18.2196C16 15.3747 13.9583 12.9514 11.1836 12.503L10.9725 12.4689Z' fill='#22223C')
          q-menu
            q-card.row.no-wrap.q-pa-md.shadow-6
              .column
                .text-h6.q-mb-md.q-mt-md.text-center Settings
                q-btn.q-my-sm(color='primary' icon="fas fa-id-badge" label='My Profile' push size='sm' v-close-popup)
                q-btn.q-my-sm(color='primary' icon="fas fa-cog" label='Settings' push size='sm' v-close-popup)
              q-separator.q-mx-lg(vertical inset)
              .column.items-center
                q-avatar(size='72px')
                  img(src='~assets/img/layout/avatar.png')
                .text-subtitle1.q-mb-xs {{ user.firstName }}
                q-btn(color='primary' @click="doLogOut()" label='Logout' push size='sm' v-close-popup)
      .mobile-svg-btn(@click="showMobileDrawer = !showMobileDrawer")
        svg(width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg')
          rect(x='7' y='12' width='26' height='3' rx='1.5' :fill="(goWhiteMenu || goSticky) ? 'black' : 'white'")
          rect(x='7' y='19' width='26' height='3' rx='1.5' :fill="(goWhiteMenu || goSticky) ? 'black' : 'white'")
          rect(x='7' y='26' width='26' height='3' rx='1.5' :fill="(goWhiteMenu || goSticky) ? 'black' : 'white'")
    .app-header-popup(
      @mouseover="popupHovered = true"
      @mouseleave="popupHovered = false; toggleInsightsMenu(false);"
    )
      insights-selector.insights--selector(@clicked="toggleInsightsMenu(false); goWhiteMenu = false;")
</template>

<style lang='sass'>
.app-header--container-white
  background: white !important
.app-header--container
  z-index: 1001
  width: 100%
  display: flex
  flex-direction: row
  justify-content: center
  top: 0
  left: 0
  background: transparent
  transition: all 0.5s ease-in-out
  @media screen and (min-width: 1024px)
    height: 90px
  @media screen and (max-width: 1024px)
    height: 80px
  .app-header--content
    display: flex
    flex-direction: column
    justify-content: center
    width: 100%
    .app-header-menu
      display: flex
      height: 100%
      flex-direction: row
      justify-content: space-between
      align-self: center
      width: calc(100% - 40px)
      max-width: 100%
      @media screen and (min-width: 1024px)
        padding: 0 20px 0 59px
      @media screen and (max-width: 1024px)
      .app-header-link
        display: flex
        justify-content: center
        align-items: center
      .app-header--logo-black
        color: black !important
        fill: black !important
      .app-header--logo
        cursor: pointer
        @media screen and (min-width: 1024px)
          width: 140px
          height: 40px
        @media screen and (max-width: 1023px)
          width: 94px
          height: 27px
      .app-header--menu-container
        display: flex
        flex-direction: row
        justify-content: center
        align-items: center
        height: 100%
        @media screen and (max-width: 1200px)
          display: none
      .mobile-svg-btn
        cursor: pointer
        display: flex
        justify-content: center
        align-items: center
        @media screen and (min-width: 1200px)
          display: none
    .app-header-popup
      width: 100%
      position: relative
      z-index: 1
      .insights--selector
        overflow: hidden
        position: absolute
        top: 0px
        left: 0px
        height: 534px
        box-shadow: 0px 13px 17px rgb(0 0 0 / 15%)
        background: #FFFFFF
        max-height: 0px
  .loggedInBtn
    background: #5FEFC0
    border-radius: 10px
    width: 48px
    height: 48px
    display: flex
    justify-content: center
    align-items: center
    cursor: pointer
  .loggedOutBtn
    display: flex
    justify-content: center
    align-items: center
    cursor: pointer
  .logoContainer
    display: flex
    align-items: center
  .appHeaderBg
    width: 1170px
    max-width: 90%
    display: flex
    justify-content: center
    align-items: center
    background: #FFFFFF
    @media screen and (min-width: 1024px)
      height: 90px
    @media screen and (max-width: 1023px)
      height: 80px
  .menuIcon
    cursor: pointer
    display: flex
    justify-content: center
    align-items: center
    background: #FFFFFF
    box-shadow: 0px 20px 40px rgba(34, 34, 60, 0.16)
    border-radius: 4px
    height: 40px
    width: 40px
  .profileIcon
    margin-left: 16px
    background: white
    border-radius: 4px
    height: 40px
    width: 40px
    cursor: pointer
    display: flex
    justify-content: center
    align-items: center
    border: 1px solid #22223C
    box-sizing: border-box
</style>
